import { UserGroupMemberRole } from '@navio/nsdk';

type MembershipSwitches = {
  isAdmin?: boolean;
  isEmployee?: boolean;
};

/**
 * An administrator of a holder organization is an employee.
 * `isEmployee` is not cleared, so turning administrator off leaves the employee role.
 */
const membershipRoleFromSwitches = ({ isAdmin, isEmployee }: MembershipSwitches): UserGroupMemberRole =>
  isAdmin || isEmployee ? UserGroupMemberRole.EMPLOYEE : UserGroupMemberRole.MEMBER;

/**
 * `role` for a membership patch. Omitted for an ordinary fleet.
 * `previous` also omits an unchanged role — the member sheet. Without it the
 * partner member form sends the role on every save.
 */
export const membershipRolePatch = ({
  canAssignMembershipRole,
  next,
  previous
}: {
  canAssignMembershipRole: boolean;
  next: MembershipSwitches;
  previous?: MembershipSwitches;
}): { role: UserGroupMemberRole } | undefined => {
  if (!canAssignMembershipRole) {
    return undefined;
  }
  const role = membershipRoleFromSwitches(next);
  if (previous && membershipRoleFromSwitches(previous) === role) {
    return undefined;
  }
  return { role };
};

/**
 * The employee switch has to flip on when administrator turns on.
 * False once it is already on, and false on demotion — that must not clear it.
 */
export const employeeForcedByAdmin = (
  canAssignMembershipRole: boolean,
  isAdmin: boolean | undefined,
  isEmployee: boolean | undefined
): boolean =>
  canAssignMembershipRole &&
  isEmployee !== true &&
  membershipRoleFromSwitches({ isAdmin, isEmployee: false }) === UserGroupMemberRole.EMPLOYEE;
